import { Suspense, lazy } from "react";
import { Outlet, type RouteObject } from "react-router-dom";
import { DefaultLayout } from "@/shared/ui/DefaultLayout";
import { AboutPage } from "@/public/features/site/pages/AboutPage";
import { BoardDetailPage } from "@/public/features/board/pages/BoardDetailPage";
import { BoardListPage } from "@/public/features/board/pages/BoardListPage";
import { HomePage } from "@/public/features/home/pages/HomePage";
import { NotFoundPage } from "@/public/features/site/pages/NotFoundPage";
import { OrgDetailPage } from "@/public/features/org/pages/OrgDetailPage";
import { OrgNewFlowPage } from "@/public/features/org/pages/OrgNewFlowPage";
import { ProblemDetailPage } from "@/public/features/problem/pages/ProblemDetailPage";
import { ProblemListPage } from "@/public/features/problem/pages/ProblemListPage";
import { ProblemNewFlowPage } from "@/public/features/problem/pages/ProblemNewFlowPage";
import { PrivacyPage } from "@/public/features/site/pages/PrivacyPage";
import { SitemapPage } from "@/public/features/site/pages/SitemapPage";
import { SolutionCasesPage } from "@/public/features/solution/pages/SolutionCasesPage";
import { SolutionDetailPage } from "@/public/features/solution/pages/SolutionDetailPage";
import { SolutionListPage } from "@/public/features/solution/pages/SolutionListPage";

const MapPage = lazy(() =>
  import("@/public/features/map/pages/MapPage").then((m) => ({ default: m.MapPage })),
);

const ScreenPreview = import.meta.env.DEV
  ? lazy(() =>
      import("#test/ScreenPreview").then((m) => ({ default: m.ScreenPreview })),
    )
  : null;

/**
 * 자기 레이아웃을 갖는 화면(`DefaultLayout` 을 쓰는 쪽)의 본문 칸.
 * **여백을 주지 않는다** — 여백은 `DefaultLayout` 안쪽이 정한다.
 */
function ScreenMain() {
  return (
    <main>
      <Outlet />
    </main>
  );
}

/**
 * 자기 레이아웃이 없는 화면(소개·처리방침·사이트맵·없는 주소)의 본문 칸.
 * 이쪽만 바깥 여백과 최대 폭을 갖는다.
 */
function PlainMain() {
  return (
    <main className="mx-auto max-w-[1120px] px-6 pt-10 pb-24 max-[600px]:px-4 max-[600px]:pt-7 max-[600px]:pb-[72px]">
      <Outlet />
    </main>
  );
}

const MAP_FALLBACK = (
  <DefaultLayout>
    <div className="layout-body p-4 text-body-md text-black-300">
      지도를 불러오는 중입니다.
    </div>
  </DefaultLayout>
);

const SCREEN_ROUTES: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: "problems", element: <ProblemListPage /> },
  {
    path: "problems/new",
    element: <ProblemNewFlowPage />,
    children: [{ path: ":step" }],
  },
  {
    path: "problems/:id/edit",
    element: <ProblemNewFlowPage />,
    children: [{ path: ":step" }],
  },
  { path: "problems/:id", element: <ProblemDetailPage /> },
  { path: "solutions", element: <SolutionListPage /> },
  { path: "solutions/:id", element: <SolutionDetailPage /> },
  { path: "solutions/:id/cases", element: <SolutionCasesPage /> },
  {
    path: "orgs/new",
    element: <OrgNewFlowPage />,
    children: [{ path: ":step" }],
  },
  { path: "orgs/:id", element: <OrgDetailPage /> },
  {
    path: "map",
    element: <Suspense fallback={MAP_FALLBACK}>{<MapPage />}</Suspense>,
  },
  { path: "board", element: <BoardListPage /> },
  { path: "board/:slug", element: <BoardDetailPage /> },
  ...(ScreenPreview === null
    ? []
    : [
        {
          path: "test",
          element: <Suspense fallback={null}>{<ScreenPreview />}</Suspense>,
        },
      ]),
];

/** 자기 레이아웃이 없어 바깥 여백을 받아야 하는 화면들. */
const PLAIN_ROUTES: RouteObject[] = [
  { path: "about", element: <AboutPage /> },
  { path: "privacy", element: <PrivacyPage /> },
  { path: "sitemap", element: <SitemapPage /> },
  { path: "*", element: <NotFoundPage /> },
];

export const PUBLIC_ROUTES: RouteObject[] = [
  { element: <ScreenMain />, children: SCREEN_ROUTES },
  { element: <PlainMain />, children: PLAIN_ROUTES },
];

/**
 * PC 에서 등록·수정 흐름을 배경 화면 위에 띄우는 라우트. 배경은 `App` 이 정하고
 * 닫으면 그 주소로 돌아간다.
 */
export function registerModalRoutes(closeTo: string): RouteObject[] {
  return [
    {
      path: "problems/new/*",
      element: <ProblemNewFlowPage closeTo={closeTo} />,
    },
    {
      path: "problems/:id/edit/*",
      element: <ProblemNewFlowPage closeTo={closeTo} />,
    },
  ];
}
